import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projects, getProjectBySlug } from '../data/projects';
import SimpleImageList from '../components/SimpleImageList';
import './ProjectPage.css';

const ProjectPage = () => {
    const { projectSlug } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Find project by slug using helper function
        const foundProject = getProjectBySlug(projectSlug);

        if (foundProject) {
            setProject(foundProject);
        } else {
            // Project not found, redirect to work page
            navigate('/work');
        }
        setIsLoading(false);
    }, [projectSlug, navigate]);

    const handleBackClick = () => {
        // Use the same navigation event system for pixel transition
        const event = new CustomEvent('navigation', { detail: { path: '/work' } });
        window.dispatchEvent(event);
    };

    if (isLoading) {
        return (
            <div className="project-page">
                <div className="project-loading">Loading...</div>
            </div>
        );
    }

    if (!project) {
        return null;
    }

    return (
        <div className="project-page">
            <div className="project-header">
                <button className="back-button" onClick={handleBackClick}>
                    ← Back to Projects
                </button>
                <h1 className="project-title">{project.title}</h1>
            </div>

            <div className="project-content">
                <div className="project-description">
                    {project.description.split('\n\n').map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                    ))}
                </div>

                {project.contentSections ? (
                    // Render interleaved sections with images
                    project.contentSections.map((section, sectionIndex) => (
                        <React.Fragment key={sectionIndex}>
                            <div className="project-section">
                                <h2 className="project-section-title">{section.title}</h2>
                                <div 
                                    className="project-section-content"
                                    dangerouslySetInnerHTML={{ __html: section.content }}
                                />
                            </div>
                            {section.imageIndices && section.imageIndices.length > 0 && (
                                <div className="project-section-images">
                                    {section.imageIndices.map((imgIndex) => (
                                        <div key={imgIndex} className="project-section-image">
                                            <img 
                                                src={project.images[imgIndex]} 
                                                alt={`${project.title} - ${section.title} ${imgIndex + 1}`}
                                                loading="lazy"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </React.Fragment>
                    ))
                ) : (
                    // Fallback to old customContent format
                    <>
                        {project.customContent && (
                            <div 
                                className="project-custom-content"
                                dangerouslySetInnerHTML={{ __html: project.customContent }}
                            />
                        )}

                        {Array.isArray(project.images) && project.images.length > 0 && (
                            <div className="project-gallery">
                                <SimpleImageList images={project.images} projectTitle={project.title} />
                            </div>
                        )}
                    </>
                )}

                {(project.videoLink || project.video) && (
                    <div className="project-video">
                        {project.videoLink && project.videoLink.includes('vimeo.com') ? (
                            <div className="video-embed">
                                <iframe 
                                    src={`https://player.vimeo.com/video/${project.videoLink.split('/').pop()}`}
                                    width="100%" 
                                    height="400" 
                                    frameBorder="0" 
                                    allow="autoplay; fullscreen; picture-in-picture" 
                                    allowFullScreen
                                    title={project.title}
                                ></iframe>
                            </div>
                        ) : project.videoLink ? (
                            <a href={project.videoLink} target="_blank" rel="noopener noreferrer" className="video-link">
                                Watch Project Video →
                            </a>
                        ) : (
                            <video
                                src={project.video}
                                poster={project.image}
                                autoPlay
                                loop
                                muted
                                playsInline
                                style={{ width: '100%', maxHeight: 480, display: 'block' }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectPage; 